<template>
  <div
    :class="{
      'flex h-[53px] shrink-0 items-center justify-between gap-4 border-b border-gray-300 bg-white px-4': true,
      'pl-2': props.image,
    }"
  >
    <div class="flex items-center gap-2 overflow-hidden">
      <button
        v-if="!showingSidebar && !props.hideSidebar"
        type="button"
        aria-label="Toggle sidebar"
        class="-m-0.5 rounded-lg p-0.5 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
        @click="toggleSidebar"
      >
        <Bars3Icon class="size-5" />
      </button>

      <img
        v-if="props.image"
        :src="props.image"
        class="size-5 rounded-md ml-1.5"
        alt="Newsletter image"
      />
      <component
        :is="iconComponent"
        v-if="props.icon"
        class="size-4 text-purple-500"
      />

      <div class="text-sm font-bold contents">
        <template v-for="(breadcrumb, idx) in props.breadcrumbs" :key="idx">
          <div
            :class="{
              'flex items-center gap-1': true,
              'text-[12px] bg-gray-100 border-y border-gray-200 px-0.5 py-0.5 -mx-1 first:ml-2 font-semibold !text-gray-600':
                breadcrumb.size === 'small',
              'rounded-l-md border-l border-gray-200 pl-2':
                idx === 0 && breadcrumb.size === 'small',
              'rounded-r-md border-r border-gray-200 pr-2 mr-1':
                breadcrumb.size === 'small' &&
                props.breadcrumbs[idx + 1]?.size !== 'small',
            }"
          >
            <component
              :is="iconMappingMini[breadcrumb.icon]"
              v-if="breadcrumb.icon"
              :class="{
                'size-4 shrink-0 text-purple-500': true,
                '!size-3': breadcrumb.size === 'small',
              }"
            />
            <RouterLink
              v-if="
                breadcrumb.state === 'route' &&
                breadcrumb.record &&
                idx !== props.breadcrumbs.length - 1
              "
              :to="breadcrumb.record"
              class="-mx-1.5 -my-1 whitespace-nowrap rounded-lg px-1.5 py-1 text-gray-400 hover:bg-gray-200 hover:text-gray-800"
            >
              {{ breadcrumb.text }}
            </RouterLink>
            <span
              v-else-if="breadcrumb.state === 'pending'"
              class="h-5 w-20 animate-pulse rounded-md bg-slate-200"
            ></span>
            <span
              v-else
              class="whitespace-nowrap text-black overflow-hidden text-ellipsis select-all"
            >
              {{ breadcrumb.text }}
            </span>
            <span
              v-if="
                idx !== props.breadcrumbs.length - 1 &&
                breadcrumb.state !== 'pending' &&
                props.breadcrumbs[idx + 1]?.size === breadcrumb.size
              "
              class="whitespace-nowrap text-gray-400"
              ><svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                class="size-4"
              >
                <path
                  fill-rule="evenodd"
                  d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
          </div>
        </template>
      </div>

      <slot />
    </div>

    <div class="flex items-center gap-2">
      <slot name="actions" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type Component, computed } from "vue";

import Bars3Icon from "@/icons/heroicons/bars-3-outline.svg";
import type { MappedIconMini } from "@/icons/icon-mapping-mini";
import { iconMappingMini } from "@/icons/icon-mapping-mini";
import { type RouteLocation } from "@/router/types";
import { useStore } from "@/store/stores/scaffolding";
export type Breadcrumb =
  | {
      state: "route";
      text: string;
      record?: RouteLocation;
      size?: "small";
      icon?: MappedIconMini;
    }
  | { state: "pending"; size?: "small"; icon?: MappedIconMini };

export type Props = {
  breadcrumbs: Breadcrumb[];
  hideSidebar?: boolean;
  image?: string;
  icon?: Component | MappedIconMini;
};

const iconComponent = computed(() => {
  if (props.icon && typeof props.icon === "string") {
    return iconMappingMini[props.icon];
  }
  return props.icon;
});

const props = defineProps<Props>();

const store = useStore();
const showingSidebar = computed(() => store.showingSidebar);
const toggleSidebar = store.toggleSidebar;
</script>
